// /anniversaries/:id — detail page (W3.1 §5.2).
//
// Sticky glass header (back / title / countdown / kebab) + AnniversaryHero
// + Siblings + metadata grid (Diary list / PhotoGallery) + MilestoneTape
// + ActivitySection.

import { useNavigate, useParams } from "react-router-dom";
import { useRef } from "react";

import { LoadingState } from "@/components/primitives/LoadingState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { PageHero } from "@/components/primitives/PageHero";
import { PageContainer, SectionStack, RowGap } from "@/components/layout";
import { AnniversaryHero } from "./components/AnniversaryHero";
import { BirthdayAgeSlot } from "./components/BirthdayAgeSlot";
import { AnniversaryMoreMenu } from "./components/AnniversaryMoreMenu";
import { AnniversaryDiaryList } from "./components/AnniversaryDiaryList";
import { AnniversaryPhotoGallery } from "./components/AnniversaryPhotoGallery";
import { MilestoneTape } from "./components/MilestoneTape";
import { ActivitySection } from "./components/ActivitySection";
import { RelatedWishesPanel } from "@/components/anniversary/RelatedWishesPanel";
import { SiblingsNav } from "./components/SiblingsNav";
import { UndoBar } from "@/components/patterns/UndoBar";
import { useUndoBar } from "@/hooks/useUndoBar";
import {
  useAnniversaryDetail,
  useAnniversariesList,
  useDeleteAnniversary,
  useRestoreAnniversary,
} from "@/lib/hooks/anniversary";
import { useAuth } from "@/lib/auth/AuthProvider";
import { countdownLabel } from "@/lib/anniversary/countdown";
import { queryKeys } from "@/lib/queries/keys";
import { useQueryClient } from "@tanstack/react-query";

export function AnniversaryDetailPage() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? "";
  const nav = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();

  const detailQuery = useAnniversaryDetail(id);
  // 100, not 200: `GET /api/v1/anniversaries` caps `limit` at 100
  // (`le=100` in anniversary/main.py), and asking for more returns 422
  // VALIDATION_FAILED. The W38 browser audit only saw this because the
  // openapi-fetch `params.query` fix finally made the limit reach the wire —
  // before it, the 200 was dropped and the page silently used the default 20.
  // A pair with >100 anniversaries will get an incomplete SiblingsNav; paging
  // that is a separate change, logged in docs/lessons-learned.md.
  const listQuery = useAnniversariesList({ limit: 100 });
  const deleteMut = useDeleteAnniversary();
  const restoreMut = useRestoreAnniversary();

  // W39.1 R1 (ADR-0080). Declared before the loading/error early-returns
  // because it is a hook — and the bar has to survive the delete, which is
  // exactly when the detail query may be re-fetching.
  const undo = useUndoBar<{ id: string }>({
    onExpire: () => nav("/anniversaries"),
  });

  const loaded = detailQuery.data;
  // W56.0 J (ADR-0201) — the delete this page performs makes the detail query
  // **404** on its next refetch. react-query keeps the last good row on a
  // background error, but the page must not become an error screen while the
  // undo window is still open — that would unmount the bar and take the way
  // back with it. Keep the row ourselves.
  const lastGoodRef = useRef(loaded);
  if (loaded) lastGoodRef.current = loaded;
  const a = loaded ?? lastGoodRef.current;

  if (detailQuery.isLoading) {
    return (
      <div data-testid="anniversary-detail-page">
        <LoadingState count={3} shape="card" />
      </div>
    );
  }
  // W56.0 J (ADR-0201) — a refetch that 404s **after our own delete** is not
  // 「找不到這個紀念日」. It is 「你剛剛刪掉了它，而撤銷條還開著」.
  if ((detailQuery.isError || !a) && !undo.item) {
    return (
      <div data-testid="anniversary-detail-page" className="p-5">
        <ErrorState
          title="找不到這個紀念日"
          description="可能已被刪除或連結已失效"
          retryLabel="回到列表"
          onRetry={() => nav("/anniversaries")}
        />
      </div>
    );
  }
  if (!a) return null;
  const isAuthor = a.author?.id === user?.id;
  const list = listQuery.data?.items ?? [];
  const days = a.days_until_next;

  const handleDelete = async () => {
    try {
      await deleteMut.mutateAsync({
        id: a.id,
        confirm: a.is_anchor_anniversary,
      });
      // W39.1 R1 (ADR-0080): stay on the page and show the inline undo bar.
      // Navigating first would destroy the trigger the bar is anchored to.
      void qc.invalidateQueries({ queryKey: queryKeys.anniversary.all });
      undo.begin({ id: a.id });
    } catch {
      // surface via toast in production — keep silent in v1.
    }
  };

  return (
    <div data-testid="anniversary-detail-page" className="flex flex-col pb-32">
      {/* Per W35.5 spec §3.3 / §2.1: use the shared PageHero primitive for
          sticky chrome consistency across modules. The back arrow lives in
          the eyebrow-leading slot; the kebab (more menu) goes in trailing. */}
      <PageHero
        // W45.0 `ADR-0115`: the eyebrow back link becomes the breadcrumb's parent
        // segment. `anniversary-back` travels with it so `probe-back-affordances`
        // and the e2e specs still reach the same control.
        crumbs={[
          { to: "/anniversaries", label: "紀念日", testId: "anniversary-back" },
          { label: "詳情" },
        ]}
        title={a.title}
        subtitle={days !== null ? countdownLabel(days) : undefined}
        trailing={
          <AnniversaryMoreMenu
            anniversaryId={a.id}
            canEdit={isAuthor}
            onEdit={() => nav(`/anniversaries/${a.id}/edit`)}
            onDelete={() => void handleDelete()}
          />
        }
      />

      <PageContainer vertical="default">
        <SectionStack density="loose">
          <AnniversaryHero anniversary={a} />

          {/* W36.1 (profile spec §3.5): a member birthday shows 「今年 N 歲」and
              the by-the-year table. `category === "personal"` is the
              BR-15 marker for a generated member-birthday row; a manually
              created anniversary on the same day is not somebody's age. */}
          {a.category === "personal" ? <BirthdayAgeSlot anniversary={a} /> : null}

          <SiblingsNav list={list} currentId={a.id} />

          <RowGap density="default" className="grid grid-cols-1 md:grid-cols-2 gap-s-4">
            <AnniversaryDiaryList anniversaryId={a.id} />
            <AnniversaryPhotoGallery />
          </RowGap>

          <MilestoneTape anniversaryId={a.id} />

          {/* W35.2: cross-module reference — show pair wishes whose
              target_date is within ±30 days of this anniversary. */}
          <RelatedWishesPanel anniversaryDate={a.date} />

          <ActivitySection anniversaryId={a.id} />
        </SectionStack>
      </PageContainer>

      {/*
        R1 (ADR-0080). `ConfirmDeleteDialog` said 「刪除後會保留 7 天的還原窗口」
        and then asked the user to press 撕下 anyway — the same "confirming
        something reversible" move ADR-0078 called out. Now the delete is
        immediate and reversible in place.
      */}
      {undo.item ? (
        <div
          data-testid="anniversary-undo-slot"
          // Same correction as the diary page: `fixed` + 108px, because the
          // first pass at `absolute` / `bottom-24` put the message under the
          // three-island pill.
          className="pointer-events-none fixed inset-x-0 bottom-[108px] z-30 flex justify-center px-4"
        >
          <div className="pointer-events-auto relative w-full max-w-md">
            <UndoBar
              open
              message="紀念日已刪除"
              onUndo={() =>
                restoreMut.mutate(undo.item!.id, {
                  // W56.0 J (ADR-0201): the bar must go when the record comes
                  // back — otherwise the page says 「紀念日已刪除」 over a record
                  // that is on screen again.
                  onSuccess: () => undo.clear(),
                })
              }
              className="inset-x-0"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}